import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

// Layout always needed
import Layout from "./Layout.jsx";
import ModuleGuard from '@/components/ModuleGuard';

// Auth pages - loaded immediately (user needs these first)
import Login from './Login';
import SetPassword from './SetPassword';
import ForgotPassword from './ForgotPassword';
import Welcome from './Welcome';

// Main dashboards - loaded immediately (first page after login)
import Home from './Home';
import TeacherDashboard from './TeacherDashboard';

// Lazy load all other pages
const DailyUpdates = lazy(() => import('./DailyUpdates'));
const Children = lazy(() => import('./Children'));
const Groups = lazy(() => import('./Groups'));
const Teachers = lazy(() => import('./Teachers'));
const Announcements = lazy(() => import('./Announcements'));
const MyChildren = lazy(() => import('./MyChildren'));
const Photos = lazy(() => import('./Photos'));
const WeeklyMenu = lazy(() => import('./WeeklyMenu'));
const Notifications = lazy(() => import('./Notifications'));
const Settings = lazy(() => import('./Settings'));
const MenuManagement = lazy(() => import('./MenuManagement'));
const PhotosManagement = lazy(() => import('./PhotosManagement'));
const ScheduleManagement = lazy(() => import('./ScheduleManagement'));
const Tenants = lazy(() => import('./Tenants'));
const Branches = lazy(() => import('./Branches'));
const Parents = lazy(() => import('./Parents'));
const PlatformDashboard = lazy(() => import('./PlatformDashboard'));
const Profile = lazy(() => import('./Profile'));
const UserSetup = lazy(() => import('./UserSetup'));
const BranchAdministrators = lazy(() => import('./BranchAdministrators'));
const OrphanedRecords = lazy(() => import('./OrphanedRecords'));
const Templates = lazy(() => import('./Templates'));
const Messages = lazy(() => import('./Messages'));
const BranchSettings = lazy(() => import('./BranchSettings'));
const TeacherMenu = lazy(() => import('./TeacherMenu'));
const BugReports = lazy(() => import('./BugReports'));
const SystemAnnouncements = lazy(() => import('./SystemAnnouncements'));
const Billing = lazy(() => import('./Billing'));
const ParentBilling = lazy(() => import('./ParentBilling'));
const Kreator = lazy(() => import('./Kreator'));
const DailyPlanManagement = lazy(() => import('./DailyPlanManagement'));
const StaffSchedule = lazy(() => import('./StaffSchedule'));
const MySchedule = lazy(() => import('./MySchedule'));

// Loading fallback component
const PageLoadingFallback = () => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
            <div className="inline-block">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
            </div>
            <p className="mt-3 text-slate-500 text-sm">Wczytywanie...</p>
        </div>
    </div>
);

// Page transition animation variants
const pageTransition = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.15, ease: 'easeInOut' }
};

const PAGES = {
    Home: Home,
    TeacherDashboard: TeacherDashboard,
    DailyUpdates: DailyUpdates,
    Children: Children,
    Groups: Groups,
    Teachers: Teachers,
    Announcements: Announcements,
    MyChildren: MyChildren,
    Photos: Photos,
    WeeklyMenu: WeeklyMenu,
    Notifications: Notifications,
    Settings: Settings,
    MenuManagement: MenuManagement,
    PhotosManagement: PhotosManagement,
    ScheduleManagement: ScheduleManagement,
    Tenants: Tenants,
    Branches: Branches,
    Parents: Parents,
    PlatformDashboard: PlatformDashboard,
    Profile: Profile,
    UserSetup: UserSetup,
    BranchAdministrators: BranchAdministrators,
    Administrators: BranchAdministrators,
    OrphanedRecords: OrphanedRecords,
    Templates: Templates,
    Messages: Messages,
    BranchSettings: BranchSettings,
    TeacherMenu: TeacherMenu,
    BugReports: BugReports,
    SystemAnnouncements: SystemAnnouncements,
    Billing: Billing,
    ParentBilling: ParentBilling,
    Kreator: Kreator,
    DailyPlanManagement: DailyPlanManagement,
    StaffSchedule: StaffSchedule,
    MySchedule: MySchedule,
}

function _getCurrentPage(url) {
    if (url.endsWith('/')) {
        url = url.slice(0, -1);
    }
    let urlLastPart = url.split('/').pop();
    if (urlLastPart.includes('?')) {
        urlLastPart = urlLastPart.split('?')[0];
    }

    const pageName = Object.keys(PAGES).find(page => page.toLowerCase() === urlLastPart.toLowerCase());
    return pageName || Object.keys(PAGES)[0];
}

function PagesContent() {
    const location = useLocation();

    // Routes that don't need Layout wrapper (auth pages - not lazy loaded)
    if (location.pathname === '/login' || location.pathname === '/set-password' || location.pathname === '/forgot-password' || location.pathname === '/welcome') {
        return (
            <AnimatePresence mode="wait">
                <motion.div
                    key={location.pathname}
                    initial={pageTransition.initial}
                    animate={pageTransition.animate}
                    exit={pageTransition.exit}
                    transition={pageTransition.transition}
                    className="min-h-screen"
                >
                    <Routes location={location}>
                        <Route path="/login" element={<Login />} />
                        <Route path="/set-password" element={<SetPassword />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/welcome" element={<Welcome />} />
                    </Routes>
                </motion.div>
            </AnimatePresence>
        );
    }

    const currentPage = _getCurrentPage(location.pathname);

    return (
        <Layout currentPageName={currentPage}>
            <Suspense fallback={<PageLoadingFallback />}>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/Home" element={<Home />} />
                    <Route path="/TeacherDashboard" element={<TeacherDashboard />} />
                    <Route path="/DailyUpdates" element={<DailyUpdates />} />
                    <Route path="/Children" element={<Children />} />
                    <Route path="/Groups" element={<Groups />} />
                    <Route path="/Teachers" element={<Teachers />} />
                    <Route path="/Announcements" element={<Announcements />} />
                    <Route path="/MyChildren" element={<MyChildren />} />
                    <Route path="/Photos" element={<ModuleGuard module="media"><Photos /></ModuleGuard>} />
                    <Route path="/WeeklyMenu" element={<ModuleGuard module="jadlospis"><WeeklyMenu /></ModuleGuard>} />
                    <Route path="/Notifications" element={<Notifications />} />
                    <Route path="/Settings" element={<Settings />} />
                    <Route path="/MenuManagement" element={<ModuleGuard module="jadlospis"><MenuManagement /></ModuleGuard>} />
                    <Route path="/PhotosManagement" element={<ModuleGuard module="media"><PhotosManagement /></ModuleGuard>} />
                    <Route path="/ScheduleManagement" element={<ScheduleManagement />} />
                    <Route path="/Tenants" element={<Tenants />} />
                    <Route path="/Branches" element={<Branches />} />
                    <Route path="/Parents" element={<Parents />} />
                    <Route path="/PlatformDashboard" element={<PlatformDashboard />} />
                    <Route path="/Profile" element={<Profile />} />
                    <Route path="/UserSetup" element={<UserSetup />} />
                    <Route path="/BranchAdministrators" element={<BranchAdministrators />} />
                    <Route path="/Administrators" element={<BranchAdministrators />} />
                    <Route path="/OrphanedRecords" element={<OrphanedRecords />} />
                    <Route path="/Templates" element={<Templates />} />
                    <Route path="/Messages" element={<Messages />} />
                    <Route path="/BranchSettings" element={<BranchSettings />} />
                    <Route path="/TeacherMenu" element={<ModuleGuard module="jadlospis"><TeacherMenu /></ModuleGuard>} />
                    <Route path="/BugReports" element={<BugReports />} />
                    <Route path="/SystemAnnouncements" element={<SystemAnnouncements />} />
                    <Route path="/Billing" element={<Billing />} />
                    <Route path="/ParentBilling" element={<ModuleGuard module="rozliczenia"><ParentBilling /></ModuleGuard>} />
                    <Route path="/Kreator" element={<Kreator />} />
                    <Route path="/DailyPlanManagement" element={<DailyPlanManagement />} />
                    <Route path="/StaffSchedule" element={<ModuleGuard module="grafik"><StaffSchedule /></ModuleGuard>} />
                    <Route path="/MySchedule" element={<ModuleGuard module="grafik"><MySchedule /></ModuleGuard>} />
                </Routes>
            </Suspense>
        </Layout>
    );
}

export default function Pages() {
    return (
        <Router>
            <PagesContent />
        </Router>
    );
}
